import type { ItemResponse, OrgUser, WarehouseResponse } from '@palaccount/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '@/api';
import { Ltr } from '@/components/amount';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Typeahead } from '@/components/ui/typeahead';
import { USERS_KEY, useOrgSetting } from '@/features/admin/api';
import { DocCurrencySelect } from '@/features/documents/doc-currency';
import { FxPanel, type FrozenRateView } from '@/features/documents/fx-panel';
import { RateField, type RateFieldDoc, type RateInput } from '@/features/documents/rate-field';
import { CommissionField, type CommissionDoc, type CommissionInput } from '@/features/sales/commission-field';
import { usePartnerBranches } from '@/features/partners/api';
import { useProjectPicker } from '@/features/projects/project-picker';
import { useCan } from '@/lib/permissions';
import { useMe } from '@/lib/session';
import { throwApiError } from '@/lib/server-messages';

/**
 * INV-F1…F6 (bug triage Oct 2026) — the header fields the three SALES editors
 * share, drawn ONCE so the quotation and the order are "the same as the sales
 * invoice in every detail" (QUO-01 / SO-02) by construction rather than by
 * three screens agreeing.
 *
 * Six fields plus the currency-and-rate cell (INV-F5 — the FxPanel moved up
 * INTO the header grid, where the circled empty space was):
 *
 *   Warehouse        — the document's DEFAULT; a line without one of its own
 *                      ships from here (INV-L3) and may still override it.
 *   Project          — PRJ-01's picker, reused (GEN-50).
 *   Customer branch  — a PICK over the chosen customer's active branches
 *                      (INV-F3: `partnerBranchId`); the server writes the
 *                      branch's name into the free-text column beside it, so
 *                      every older reader keeps working. Resets when the
 *                      customer changes. Without a `partnerId` prop the cell
 *                      falls back to the free-text box.
 *   Manual ref / Book no — the hand-written reference and booklet number.
 *   Salesperson      — a user; defaults to WHOEVER IS TYPING a new document.
 *
 * The cells are plain grid children, so each editor's own header grid lays
 * them out; at phone width they wrap exactly like the fields already there.
 */
export interface SalesHeaderState {
  warehouseId: string;
  projectId: string;
  /** INV-F3: the branch RECORD ('' = none); `partnerBranch` is its name, or legacy free text. */
  partnerBranchId: string;
  partnerBranch: string;
  manualRef: string;
  bookNo: string;
  salespersonUserId: string;
}

/**
 * The header's NAMES as the server resolved them — what the three money
 * documents AND the delivery note (wave 5B) carry. The money documents add
 * the frozen-rate view beside it (`SalesHeaderDoc`); a delivery note has no
 * currency and no rate, so it hands over this half alone.
 */
export interface SalesHeaderNames {
  warehouseId: string | null;
  warehouseName: string | null;
  projectId: string | null;
  projectName: string | null;
  partnerBranchId: string | null;
  partnerBranchName: string | null;
  partnerBranch: string | null;
  manualRef: string | null;
  bookNo: string | null;
  salespersonUserId: string | null;
  salespersonName: string | null;
}

/** The server's shape — the nine response fields the three contracts spread. */
export interface SalesHeaderDoc extends SalesHeaderNames, FrozenRateView {
  warehouseId: string | null;
  warehouseName: string | null;
  projectId: string | null;
  projectName: string | null;
  partnerBranchId: string | null;
  partnerBranchName: string | null;
  partnerBranch: string | null;
  manualRef: string | null;
  bookNo: string | null;
  salespersonUserId: string | null;
  salespersonName: string | null;
}

export const emptySalesHeader = (): SalesHeaderState => ({
  warehouseId: '',
  projectId: '',
  partnerBranchId: '',
  partnerBranch: '',
  manualRef: '',
  bookNo: '',
  salespersonUserId: '',
});

export function salesHeaderFromDoc(doc: SalesHeaderNames): SalesHeaderState {
  return {
    warehouseId: doc.warehouseId ?? '',
    projectId: doc.projectId ?? '',
    partnerBranchId: doc.partnerBranchId ?? '',
    // The record's CURRENT name when one is picked; the stored text otherwise
    // (a document written before branches were records).
    partnerBranch: doc.partnerBranchName ?? doc.partnerBranch ?? '',
    manualRef: doc.manualRef ?? '',
    bookNo: doc.bookNo ?? '',
    salespersonUserId: doc.salespersonUserId ?? '',
  };
}

/** The CREATE body: empties omitted (the server defaults the salesperson). */
export function salesHeaderCreateBody(state: SalesHeaderState) {
  return {
    ...(state.warehouseId ? { warehouseId: state.warehouseId } : {}),
    ...(state.projectId ? { projectId: state.projectId } : {}),
    ...(state.partnerBranchId ? { partnerBranchId: state.partnerBranchId } : {}),
    ...(state.partnerBranch.trim() ? { partnerBranch: state.partnerBranch.trim() } : {}),
    ...(state.manualRef.trim() ? { manualRef: state.manualRef.trim() } : {}),
    ...(state.bookNo.trim() ? { bookNo: state.bookNo.trim() } : {}),
    ...(state.salespersonUserId ? { salespersonUserId: state.salespersonUserId } : {}),
  };
}

/** The PATCH body: null clears, so a field emptied on screen empties on the row. */
export function salesHeaderPatchBody(state: SalesHeaderState) {
  return {
    warehouseId: state.warehouseId || null,
    projectId: state.projectId || null,
    partnerBranchId: state.partnerBranchId || null,
    partnerBranch: state.partnerBranch.trim() || null,
    manualRef: state.manualRef.trim() || null,
    bookNo: state.bookNo.trim() || null,
    salespersonUserId: state.salespersonUserId || null,
  };
}

/**
 * Bill header parity (2026-10-30) — the bill's server shape: no header
 * warehouse (its receiving warehouse rides each line) and a PURCHASER where
 * the sales documents have a salesperson.
 */
export interface BillHeaderNames {
  projectId: string | null;
  projectName: string | null;
  partnerBranchId: string | null;
  partnerBranchName: string | null;
  partnerBranch: string | null;
  manualRef: string | null;
  bookNo: string | null;
  purchaserUserId: string | null;
  purchaserName: string | null;
}

/** The bill seen through the sales header's names (the purchaser in the person slot). */
export function billHeaderView<T extends BillHeaderNames>(bill: T): T & SalesHeaderNames {
  return {
    ...bill,
    warehouseId: null,
    warehouseName: null,
    salespersonUserId: bill.purchaserUserId,
    salespersonName: bill.purchaserName,
  };
}

/** The bill's CREATE body from the shared state (the person is the purchaser). */
export function billHeaderCreateBody(state: SalesHeaderState) {
  const { warehouseId: _warehouse, salespersonUserId, ...rest } = salesHeaderCreateBody(state);
  return { ...rest, ...(salespersonUserId ? { purchaserUserId: salespersonUserId } : {}) };
}

/** The bill's PATCH body: null clears. */
export function billHeaderPatchBody(state: SalesHeaderState) {
  const { warehouseId: _warehouse, salespersonUserId, ...rest } = salesHeaderPatchBody(state);
  return { ...rest, purchaserUserId: salespersonUserId };
}

/**
 * The header's state, with INV-F6's default: a NEW document's salesperson is
 * the signed-in user, filled in once the session has loaded and only while
 * the field is still empty — a saved document's own value, adopted by the
 * editor, is never overwritten.
 */
export function useSalesHeaderState(
  isNew: boolean,
): [SalesHeaderState, Dispatch<SetStateAction<SalesHeaderState>>] {
  const [state, setState] = useState<SalesHeaderState>(emptySalesHeader);
  const { data: me } = useMe();
  const meId = me?.user.id;
  useEffect(() => {
    if (!isNew || meId === undefined) return;
    setState((current) =>
      current.salespersonUserId === '' ? { ...current, salespersonUserId: meId } : current,
    );
  }, [isNew, meId]);
  return [state, setState];
}

/** INV-L1 / INV-L2 — which optional grid columns this company draws. */
export function useLineColumns(): { bonusOn: boolean; discountOn: boolean } {
  const bonusOn = useOrgSetting<boolean>('invoicing.line_bonus') === true;
  const discountOn = useOrgSetting<boolean>('invoicing.line_discount') !== false;
  return { bonusOn, discountOn };
}

/**
 * The two cells a document may leave OUT: the delivery note (wave 5B) draws
 * its own required warehouse select and has no currency at all, so it mounts
 * the shared fields without either.
 */
export type SalesHeaderCell = 'warehouse' | 'currency';

export function SalesHeaderFields({
  idPrefix,
  state,
  onChange,
  disabled,
  doc,
  currency,
  warehouses,
  partnerId,
  omit = [],
  rate,
  commission,
  side = 'sales',
  currencyPicker,
}: {
  /**
   * Bill header parity (2026-10-30): the same cells on the BUYING side — the
   * branch is the supplier's and the person is the purchaser.
   */
  side?: 'sales' | 'purchase' | undefined;
  /**
   * Per-document currency (2026-10-30): the currency cell becomes a dropdown
   * (`features/documents/doc-currency`). Absent = the code is shown read-only.
   */
  currencyPicker?: { onChange: (code: string) => void; disabled: boolean } | undefined;
  /** Wave 4D — the editor's commission box (`useCommissionInput`); absent = no cell. */
  commission?: CommissionInput | undefined;
  /** Wave 4D — the editor's rate box (`useRateInput`); absent = no Rate cell. */
  rate?: RateInput | undefined;
  idPrefix: string;
  state: SalesHeaderState;
  onChange: (patch: Partial<SalesHeaderState>) => void;
  disabled: boolean;
  /** The saved document, for the names it resolved and (money documents) the frozen rate. */
  doc: (SalesHeaderNames & Partial<FrozenRateView>) | undefined;
  /** The document's currency; absent on a document that has none (the delivery note). */
  currency?: string | undefined;
  warehouses: readonly WarehouseResponse[] | undefined;
  /** INV-F3: the document's customer ('' = none yet) — whose branches the cell offers. */
  partnerId?: string | undefined;
  /** Cells this editor draws itself or has no use for. */
  omit?: readonly SalesHeaderCell[] | undefined;
}) {
  const { t } = useTranslation();
  const activeWarehouses = (warehouses ?? []).filter((warehouse) => warehouse.isActive);
  // BR-MD-01-07's shape: the picker OFFERS the active, DISPLAYS whatever the
  // saved document names even after it was switched off.
  const heldWarehouseMissing =
    state.warehouseId !== '' && !activeWarehouses.some((w) => w.id === state.warehouseId);
  // The FxPanel reads the frozen-rate view; a document that carries none
  // (the delivery note omits the cell anyway) hands it nothing.
  const rateView: FrozenRateView | undefined =
    doc !== undefined && typeof doc.currency === 'string' && typeof doc.baseCurrency === 'string'
      ? (doc as SalesHeaderNames & FrozenRateView)
      : undefined;

  return (
    <>
      {omit.includes('warehouse') ? null : (
        <div className="grid gap-1.5">
          <Label htmlFor={`${idPrefix}-warehouse`}>{t('sales.fWarehouse')}</Label>
          <NativeSelect
            id={`${idPrefix}-warehouse`}
            value={state.warehouseId}
            onChange={(event) => onChange({ warehouseId: event.target.value })}
            disabled={disabled}
          >
            <option value="">{t('sales.noWarehouse')}</option>
            {activeWarehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
            {heldWarehouseMissing ? (
              <option value={state.warehouseId}>{doc?.warehouseName ?? state.warehouseId}</option>
            ) : null}
          </NativeSelect>
        </div>
      )}
      <ProjectField
        id={`${idPrefix}-project`}
        value={state.projectId}
        heldName={doc?.projectName ?? null}
        onChange={(projectId) => onChange({ projectId })}
        disabled={disabled}
      />
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-branch`}>
          {side === 'purchase' ? t('bills.fSupplierBranch') : t('sales.fCustomerBranch')}
        </Label>
        {partnerId === undefined ? (
          <Input
            id={`${idPrefix}-branch`}
            value={state.partnerBranch}
            maxLength={120}
            onChange={(event) => onChange({ partnerBranch: event.target.value })}
            disabled={disabled}
          />
        ) : (
          <BranchSelect
            id={`${idPrefix}-branch`}
            partnerId={partnerId}
            value={state.partnerBranchId}
            heldName={state.partnerBranch}
            onChange={(partnerBranchId, partnerBranch) => onChange({ partnerBranchId, partnerBranch })}
            disabled={disabled}
          />
        )}
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-manual-ref`}>{t('sales.fManualRef')}</Label>
        <Input
          id={`${idPrefix}-manual-ref`}
          value={state.manualRef}
          maxLength={60}
          onChange={(event) => onChange({ manualRef: event.target.value })}
          disabled={disabled}
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-book-no`}>{t('sales.fBookNo')}</Label>
        <Input
          id={`${idPrefix}-book-no`}
          value={state.bookNo}
          maxLength={40}
          onChange={(event) => onChange({ bookNo: event.target.value })}
          disabled={disabled}
        />
      </div>
      <SalespersonField
        id={`${idPrefix}-salesperson`}
        value={state.salespersonUserId}
        heldName={doc?.salespersonName ?? null}
        onChange={(salespersonUserId) => onChange({ salespersonUserId })}
        disabled={disabled}
        label={side === 'purchase' ? t('bills.fPurchaser') : t('sales.fSalesperson')}
        noneLabel={side === 'purchase' ? t('bills.noPurchaser') : t('sales.noSalesperson')}
      />
      {/* Wave 4D — the commission %, beside the rep (features/sales/commission-field). */}
      {commission === undefined ? null : (
        <CommissionField
          idPrefix={idPrefix}
          doc={doc !== undefined && 'commissionPct' in doc ? (doc as unknown as CommissionDoc) : undefined}
          input={commission}
          disabled={disabled}
        />
      )}
      {/* INV-F5 — currency and rate IN the header. The code always; the
          FxPanel only when the document converts (GEN-116: a base-currency
          document shows no conversion and no rate). */}
      {omit.includes('currency') || currency === undefined ? null : (
        <div className="grid gap-1.5">
          <Label htmlFor={currencyPicker === undefined ? undefined : `${idPrefix}-currency`}>
            {t('sales.fCurrencyRate')}
          </Label>
          <div className="grid gap-2 rounded-[11px] border border-input bg-muted/40 px-3 py-2 text-sm">
            {currencyPicker === undefined ? (
              <Ltr value={currency} className="font-mono" />
            ) : (
              <DocCurrencySelect
                id={`${idPrefix}-currency`}
                value={currency}
                onChange={currencyPicker.onChange}
                disabled={currencyPicker.disabled}
              />
            )}
            <FxPanel doc={rateView} />
          </div>
        </div>
      )}
      {/* Wave 4D — the Rate, split out of the currency cell: editable on a
          draft, the market rate as its hint (features/documents/rate-field). */}
      {omit.includes('currency') || currency === undefined || rate === undefined ? null : (
        <RateField
          idPrefix={idPrefix}
          currency={currency}
          doc={doc !== undefined && 'rateOverride' in doc ? (doc as unknown as RateFieldDoc) : undefined}
          input={rate}
          disabled={disabled}
        />
      )}
    </>
  );
}

/** A saved document's free-text branch with no record behind it (pre-INV-F3). */
const LEGACY_TEXT = '__legacy_text__';

/**
 * INV-F3 — the customer's branch, a select over the chosen partner's ACTIVE
 * branches. BR-MD-01-07's shape one level down: the picker OFFERS the active,
 * DISPLAYS whatever the saved document names (a switched-off branch, or the
 * free text of a document written before branches were records). Changing
 * the customer empties the cell: a branch is a fact about ONE customer.
 */
function BranchSelect({
  id,
  partnerId,
  value,
  heldName,
  onChange,
  disabled,
}: {
  id: string;
  partnerId: string;
  value: string;
  heldName: string;
  onChange: (partnerBranchId: string, partnerBranch: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const { data: branches } = usePartnerBranches(partnerId || undefined, true);
  const active = (branches ?? []).filter((branch) => branch.isActive);

  // The reset: only when the customer CHANGES from one to another (or to
  // none) — never on the first customer a loading document announces, which
  // may arrive a render after the header it belongs to.
  const previousPartner = useRef(partnerId);
  useEffect(() => {
    const previous = previousPartner.current;
    previousPartner.current = partnerId;
    if (previous !== '' && previous !== partnerId && (value !== '' || heldName !== '')) {
      onChange('', '');
    }
  }, [partnerId, value, heldName, onChange]);

  const heldMissing = value !== '' && !active.some((branch) => branch.id === value);
  const legacy = value === '' && heldName !== '';
  const selectValue = legacy ? LEGACY_TEXT : value;

  return (
    <NativeSelect
      id={id}
      value={selectValue}
      onChange={(event) => {
        const picked = event.target.value;
        if (picked === LEGACY_TEXT) return;
        const branch = active.find((row) => row.id === picked);
        onChange(picked, branch?.name ?? '');
      }}
      disabled={disabled || partnerId === ''}
    >
      <option value="">{t('sales.noBranch')}</option>
      {active.map((branch) => (
        <option key={branch.id} value={branch.id}>
          {branch.name}
        </option>
      ))}
      {heldMissing ? <option value={value}>{heldName || value}</option> : null}
      {legacy ? <option value={LEGACY_TEXT}>{heldName}</option> : null}
    </NativeSelect>
  );
}

/**
 * INV-F2 — PRJ-01's own picker (GEN-50). Mounted only for a reader who may
 * list projects: the hook asks the server on mount, and a reader without
 * `projects.view` would be asking for a 403 on every editor open. They keep
 * a read-only field with the name the server resolved.
 */
function ProjectField({
  id,
  value,
  heldName,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  heldName: string | null;
  onChange: (projectId: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const canList = useCan('projects.view');
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{t('sales.fProject')}</Label>
      {canList ? (
        <ProjectTypeahead id={id} value={value} onChange={onChange} disabled={disabled} />
      ) : (
        <div className="flex h-12 items-center rounded-[11px] border border-input bg-muted/40 px-3 text-sm">
          {heldName ?? '—'}
        </div>
      )}
    </div>
  );
}

function ProjectTypeahead({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (projectId: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const picker = useProjectPicker(value || null);
  return (
    <Typeahead
      id={id}
      label={t('sales.fProject')}
      value={value || null}
      onChange={(projectId) => onChange(projectId ?? '')}
      options={picker.options}
      selected={picker.optionFor(value)}
      query={picker.query}
      onQueryChange={picker.setQuery}
      placeholder={t('sales.pickProject')}
      emptyLabel={t('picker.noMatchingProject')}
      clearLabel={t('sales.noProject')}
      loading={picker.loading}
      disabled={disabled}
    />
  );
}

/**
 * INV-F6 — the salesperson, a USER. The user list is `admin.view`
 * (ADM-05), which most sellers do not hold, so the select is built from what
 * the reader CAN know: the whole list when they may read it, otherwise
 * themselves (the default on a new document) and whoever the saved document
 * already names, with the name the server resolved.
 */
function SalespersonField({
  id,
  value,
  heldName,
  onChange,
  disabled,
  label,
  noneLabel,
}: {
  id: string;
  value: string;
  heldName: string | null;
  onChange: (userId: string) => void;
  disabled: boolean;
  /** "Salesperson" on the sales side, "Purchaser" on the bill. */
  label: string;
  noneLabel: string;
}) {
  const canList = useCan('admin.view');
  const { data: me } = useMe();
  const users = useQuery({
    queryKey: USERS_KEY,
    enabled: canList,
    queryFn: async (): Promise<OrgUser[]> => {
      const response = await api.users.list();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.users;
    },
  });

  const options = new Map<string, string>();
  for (const user of users.data ?? []) {
    if (user.status === 'active') options.set(user.id, user.name || user.email);
  }
  if (me !== undefined && me !== null && !options.has(me.user.id)) {
    options.set(me.user.id, me.user.name || me.user.email);
  }
  // A stored salesperson who has since left (or whom this reader cannot list)
  // still DISPLAYS — the offer rule governs new picks, not history.
  if (value !== '' && !options.has(value)) options.set(value, heldName ?? value);

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <NativeSelect
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      >
        <option value="">{noneLabel}</option>
        {[...options.entries()].map(([userId, name]) => (
          <option key={userId} value={userId}>
            {name}
          </option>
        ))}
      </NativeSelect>
    </div>
  );
}

/**
 * INV-L4 — the per-line Unit select: the item's stock unit (the default, and
 * what an empty value means — the server stores NULL and reads it as the
 * stock unit) plus every unit of the item's own table with Sell allowed (MD-03
 * panel 5, R6). Nothing to choose on a free-text line.
 */
export function LineUnitSelect({
  item,
  value,
  onChange,
  disabled,
  label,
  use = 'sell',
}: {
  item: Pick<ItemResponse, 'stockUnitId' | 'stockUnitName' | 'stockUnitSymbol' | 'units'> | undefined;
  value: string;
  onChange: (unitId: string) => void;
  disabled: boolean;
  label: string;
  /**
   * Wave 4C — which of the item's units the line may be typed in: a sales
   * line offers the units allowed for SELLING, a purchase line (PO, bill)
   * the units allowed for BUYING — the same check the server makes.
   */
  use?: 'sell' | 'buy';
}) {
  if (item === undefined) return <span className="px-2 text-sm text-muted-foreground">—</span>;
  const sellUnits = (item.units ?? []).filter(
    (unit) => (use === 'buy' ? unit.allowBuy : unit.allowSell) && unit.unitId !== item.stockUnitId,
  );
  return (
    <NativeSelect
      aria-label={label}
      className="h-9 text-sm"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
    >
      <option value="">{item.stockUnitSymbol || item.stockUnitName}</option>
      {sellUnits.map((unit) => (
        <option key={unit.unitId} value={unit.unitId}>
          {unit.unitSymbol || unit.unitName}
        </option>
      ))}
    </NativeSelect>
  );
}
